/**
 * The addresses the schema keeps for media, mirrored from Farfalla's nodes:
 * the reader is served over https, so an http or relative image, video or
 * embed is mixed content the browser blocks and the server drops. An editor
 * that showed one would have the writer save it only to lose it.
 */
export const isHttps = (url: string): boolean => /^https:/i.test(url.trim());

// Of a data: source, Farfalla keeps only an inline raster image.
const RASTER_DATA_IMAGE = /^data:image\/(png|jpe?g|gif|webp|avif);/;

/** Mirror of Farfalla's SafeImage::hasSafeSource. */
export const hasSafeImageSource = (src: string): boolean => {
    const source = src.trim().toLowerCase();

    return source.startsWith('https://') || RASTER_DATA_IMAGE.test(source);
};

/**
 * The address a video is judged by: its own src, else its first https
 * <source>, else the first <source> with any address, so what is refused is
 * named by the scheme that was tried. Empty when the video has none.
 */
export function videoSource(element: Element): string {
    const src = (element.getAttribute('src') ?? '').trim();
    if (src !== '') return src;

    let fallback = '';

    for (const source of element.querySelectorAll('source')) {
        const sourceSrc = (source.getAttribute('src') ?? '').trim();
        if (isHttps(sourceSrc)) return sourceSrc;
        if (sourceSrc !== '' && fallback === '') fallback = sourceSrc;
    }

    return fallback;
}

/** The scheme of an address as the server reports it: `relative` when it has none. */
export const schemeOf = (url: string): string =>
    url
        .trim()
        .match(/^([a-z][a-z0-9+.-]*):/i)?.[1]
        .toLowerCase() ?? 'relative';

export interface RefusedSource {
    node: string;
    scheme: string;
}

/**
 * The media whose address the schema refuses, named by its scheme: what the
 * server's scanner reports as `unsafe_url`, so a paste says in the moment
 * what a save would say. A video is judged by its source and its poster, an
 * image also by the inline raster rule. Links are not media: the link mark
 * takes http.
 */
export function refusedSources(root: ParentNode, tags: string[]): RefusedSource[] {
    if (tags.length === 0) return [];

    const refused: RefusedSource[] = [];

    for (const element of root.querySelectorAll(tags.join(','))) {
        const node = element.tagName.toLowerCase();
        const urls =
            node === 'video'
                ? [videoSource(element), element.getAttribute('poster') ?? '']
                : [element.getAttribute('src') ?? ''];

        for (const url of urls) {
            if (url.trim() === '') continue;
            if (node === 'img' ? hasSafeImageSource(url) : isHttps(url)) continue;

            refused.push({ node, scheme: schemeOf(url) });
        }
    }

    return refused;
}
